import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, RefreshCw, TrendingUp, TrendingDown, Users, Trophy, Clock, Target } from 'lucide-react';

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

const LOSS_COLORS = {
  'Price High': '#ef4444',
  'Design Not Suitable': '#f97316',
  'Waiting for Budget': '#eab308',
  'Competitor Purchased': '#8b5cf6',
  'Stock Not Available': '#3b82f6',
  'Occasion Cancelled': '#6b7280',
  'Other': '#a3a3a3',
};

const SalesmanAnalytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/salesman-analytics?period=${period}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [period]);

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  const overall = data?.overall || {};
  const leaderboard = data?.leaderboard || [];
  const globalLoss = data?.global_loss_reasons || {};
  const totalLossReasons = Object.values(globalLoss).reduce((a, b) => a + b, 0);

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="salesman-analytics-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">Salesman Performance</h1>
              <p className="text-xs text-emerald-200">{leaderboard.length} salesmen tracked</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36 h-8 bg-emerald-700 border-emerald-600 text-white text-xs" data-testid="period-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Overall Stats */}
        <div className="px-4 pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="overall-stats">
            <button
              onClick={() => navigate('/lead-sessions?lead_status=won')}
              className="bg-white rounded-lg border p-3 text-left hover:bg-emerald-50 hover:border-emerald-200 transition-colors cursor-pointer"
              data-testid="card-conversion"
            >
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Conversion Rate</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{overall.conversion_rate || 0}%</p>
              <p className="text-xs text-gray-400">{overall.total_won || 0} won / {(overall.total_won || 0) + (overall.total_lost || 0)} closed</p>
              <p className="text-[10px] text-emerald-500 mt-1 font-medium">View won leads →</p>
            </button>
            <button
              onClick={() => navigate('/lead-sessions?lead_status=won')}
              className="bg-white rounded-lg border p-3 text-left hover:bg-green-50 hover:border-green-200 transition-colors cursor-pointer"
              data-testid="card-revenue-won"
            >
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-green-500" />
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Revenue Won</p>
              </div>
              <p className="text-2xl font-bold text-green-600 mt-1">{fmt(overall.total_won_value)}</p>
              <p className="text-xs text-gray-400">{overall.total_won || 0} leads</p>
              <p className="text-[10px] text-green-500 mt-1 font-medium">View won leads →</p>
            </button>
            <button
              onClick={() => navigate('/lead-sessions?lead_status=lost')}
              className="bg-white rounded-lg border p-3 text-left hover:bg-red-50 hover:border-red-200 transition-colors cursor-pointer"
              data-testid="card-revenue-lost"
            >
              <div className="flex items-center gap-1">
                <TrendingDown className="w-3 h-3 text-red-500" />
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Revenue Lost</p>
              </div>
              <p className="text-2xl font-bold text-red-500 mt-1">{fmt(overall.total_lost_value)}</p>
              <p className="text-xs text-gray-400">{overall.total_lost || 0} leads</p>
              <p className="text-[10px] text-red-400 mt-1 font-medium">View lost leads →</p>
            </button>
            <button
              onClick={() => navigate('/lead-sessions')}
              className="bg-white rounded-lg border p-3 text-left hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer"
              data-testid="card-total-leads"
            >
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Total Leads</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{overall.total_leads || 0}</p>
              <p className="text-xs text-gray-400">across all salesmen</p>
              <p className="text-[10px] text-gray-400 mt-1 font-medium">View all sessions →</p>
            </button>
          </div>
        </div>

        {/* Global Loss Reasons */}
        {totalLossReasons > 0 && (
          <div className="px-4 pt-4">
            <div className="bg-white rounded-lg border p-4">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Why Leads Are Lost</p>
              <div className="space-y-2">
                {Object.entries(globalLoss).sort((a, b) => b[1] - a[1]).map(([reason, count]) => {
                  const pct = Math.round((count / totalLossReasons) * 100);
                  return (
                    <div key={reason}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-700 font-medium">{reason}</span>
                        <span className="text-gray-500">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: LOSS_COLORS[reason] || '#6b7280' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        <div className="px-4 pt-4 pb-4">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1">
            <Trophy className="w-4 h-4 text-amber-500" /> Salesman Leaderboard
          </p>
          <div className="space-y-3" data-testid="leaderboard">
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No salesman data available</p>
              </div>
            ) : leaderboard.map((s, rank) => (
              <div key={s.salesman_id} className="bg-white rounded-lg border overflow-hidden" data-testid={`salesman-card-${s.salesman_id}`}>
                <button
                  onClick={() => setExpandedId(prev => prev === s.salesman_id ? null : s.salesman_id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                      rank === 0 ? 'bg-amber-100 text-amber-700' :
                      rank === 1 ? 'bg-gray-200 text-gray-600' :
                      rank === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      #{rank + 1}
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm text-gray-900">{s.salesman_name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span>{s.total_leads} leads</span>
                        <span className="flex items-center gap-0.5 text-green-600 font-medium">
                          <Target className="w-3 h-3" />{s.conversion_rate}%
                        </span>
                        {s.avg_close_days > 0 && (
                          <span className="flex items-center gap-0.5 text-gray-400">
                            <Clock className="w-3 h-3" />{s.avg_close_days}d avg
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-700">{fmt(s.won_value)}</p>
                    <div className="flex gap-1 mt-0.5 justify-end">
                      {s.won > 0 && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">{s.won}W</span>}
                      {s.lost > 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">{s.lost}L</span>}
                      {s.parked > 0 && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">{s.parked}P</span>}
                      {s.open > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">{s.open}O</span>}
                    </div>
                  </div>
                </button>

                {/* Expanded Details */}
                {expandedId === s.salesman_id && (
                  <div className="border-t px-4 py-3 bg-gray-50 space-y-3">
                    {/* Quick-action drill-down buttons */}
                    <div className="grid grid-cols-4 gap-1.5">
                      <button onClick={() => navigate(`/lead-sessions?lead_status=won&salesman=${encodeURIComponent(s.salesman_name)}`)}
                        className="text-center py-1.5 bg-green-50 text-green-700 rounded-lg text-[10px] font-semibold hover:bg-green-100 transition-colors">
                        {s.won} Won
                      </button>
                      <button onClick={() => navigate(`/lead-sessions?lead_status=lost&salesman=${encodeURIComponent(s.salesman_name)}`)}
                        className="text-center py-1.5 bg-red-50 text-red-600 rounded-lg text-[10px] font-semibold hover:bg-red-100 transition-colors">
                        {s.lost} Lost
                      </button>
                      <button onClick={() => navigate(`/lead-sessions?lead_status=parked&salesman=${encodeURIComponent(s.salesman_name)}`)}
                        className="text-center py-1.5 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-semibold hover:bg-amber-100 transition-colors">
                        {s.parked} Parked
                      </button>
                      <button onClick={() => navigate(`/lead-sessions?lead_status=open&salesman=${encodeURIComponent(s.salesman_name)}`)}
                        className="text-center py-1.5 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-semibold hover:bg-blue-100 transition-colors">
                        {s.open} Open
                      </button>
                    </div>
                    {/* Revenue breakdown */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="bg-white rounded-lg border p-2 text-center">
                        <p className="text-[10px] text-gray-500 font-medium">Won</p>
                        <p className="text-sm font-bold text-green-600">{fmt(s.won_value)}</p>
                      </div>
                      <div className="bg-white rounded-lg border p-2 text-center">
                        <p className="text-[10px] text-gray-500 font-medium">Lost</p>
                        <p className="text-sm font-bold text-red-500">{fmt(s.lost_value)}</p>
                      </div>
                      <div className="bg-white rounded-lg border p-2 text-center">
                        <p className="text-[10px] text-gray-500 font-medium">Pipeline</p>
                        <p className="text-sm font-bold text-blue-600">{fmt(s.pipeline_value)}</p>
                      </div>
                    </div>

                    {/* Conversion Funnel */}
                    <div className="bg-white rounded-lg border p-3">
                      <div className="flex h-5 rounded-full overflow-hidden gap-0.5">
                        {s.won > 0 && <div className="bg-green-500 transition-all" style={{ width: `${(s.won / s.total_leads) * 100}%` }} title={`Won: ${s.won}`} />}
                        {s.lost > 0 && <div className="bg-red-400 transition-all" style={{ width: `${(s.lost / s.total_leads) * 100}%` }} title={`Lost: ${s.lost}`} />}
                        {s.parked > 0 && <div className="bg-amber-400 transition-all" style={{ width: `${(s.parked / s.total_leads) * 100}%` }} title={`Parked: ${s.parked}`} />}
                        {s.open > 0 && <div className="bg-blue-400 transition-all" style={{ width: `${(s.open / s.total_leads) * 100}%` }} title={`Open: ${s.open}`} />}
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Won {s.won}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />Lost {s.lost}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Parked {s.parked}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />Open {s.open}</span>
                      </div>
                    </div>

                    {/* Loss Reasons for this salesman */}
                    {Object.keys(s.loss_reasons || {}).length > 0 && (
                      <div className="bg-white rounded-lg border p-3">
                        <p className="text-xs font-medium text-gray-600 mb-2">Loss Reasons</p>
                        <div className="space-y-1">
                          {Object.entries(s.loss_reasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                            <div key={reason} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">{reason}</span>
                              <span className="font-medium text-red-600">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesmanAnalytics;
